/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  isEmpty,
  isFunction,
  isPlainObject,
  pick,
  toPlainObject,
} from '@pogokid/util/lodash'
import { isArrayWithLength } from '@pogokid/util'
import { type SchemaObject } from 'ajv'

export interface CleanValuesOptions {
  /**
   * Convert each property to a JS primitive
   */
  toPrimitive?: boolean
  /**
   * Optional predicate for each property to control whether
   * it should be cleaned
   */
  shouldCleanProperties?: (
    propertyName: string,
    value: any
  ) => boolean
  /**
   * Pre-process object before it's properties
   * are cleaned
   *
   * @example
   * ```ts
   * properties: function(
   *   values: Record<any, any>
   * ) {
   *   if (
   *     values &&
   *     'type' in values &&
   *     !('title' in values) &&
   *     values.type === GENERIC_SCHEDULE_EVENT_TYPE
   *   ) {
   *     return { type: GENERIC_SCHEDULE_EVENT_TYPE }
   *   }
   *   return values
   * }
   * ```
   */
  properties?:
    | (<T extends Record<any, any>>(
        values: T,
        options: CleanValuesOptions
      ) => T)
    /** @deprecated */
    | string[]
    | null
}

export function schemaProperties(schema: SchemaObject) {
  return !isEmpty(schema.properties)
    ? Object.keys(schema.properties)
    : []
}

/**
 * Make sure an object only includes properties
 * on the original schema
 */
export function makePropertyCleaner<T extends object>(
  properties: string[]
) {
  return (values: T): T =>
    pick(properties as Array<keyof T>, values) as T
}

/**
 * Recursively normalise a values object before it is handed to schema
 * validation, returning a cleaned copy (the input is never mutated).
 *
 * Per property it will:
 * - **Trim strings.** Every string is `.trim()`-ed.
 * - **Pass numbers and booleans through untouched**, including the
 *   falsy `0` and `false`.
 * - **Recurse into objects and arrays**, applying the same rules at
 *   every depth.
 *
 * Opinionated / potentially surprising behaviour:
 * - **Empties become `null`.** Any value that ends up "empty" per
 *   lodash `isEmpty` is collapsed to `null` — so a whitespace-only
 *   string (`'   '`), an empty object (`{}`), `undefined` and `null`
 *   all return `null`. **Empty arrays are the deliberate exception**:
 *   `[]` is preserved rather than nulled. Note this also means an empty
 *   object *element inside an array* becomes a `null` element (e.g.
 *   `[{}]` -> `[null]`).
 * - **`properties` as a `string[]` only filters the root.** The
 *   deprecated array form whitelists keys on the top-level object only;
 *   nested objects are left with all their keys. Prefer the function
 *   form (`(values, options) => values`) — it is re-applied at every
 *   object depth, so it can prune/transform nested objects too.
 * - **`shouldCleanProperties` returning `false` skips a property
 *   entirely.** That property keeps its original value verbatim (an
 *   untrimmed string, an un-recursed object), it is not merely left in
 *   place after cleaning.
 * - **`toPrimitive` mainly matters for non-plain objects.** Plain
 *   objects are already rebuilt as plain objects by the recursion;
 *   `toPrimitive` additionally converts class instances / non-plain
 *   objects to plain objects via lodash `toPlainObject`.
 *
 * @see {@link cleanValue} for the per-value implementation.
 */
export function cleanValues<T extends Record<any, any>>(
  values: T,
  options: CleanValuesOptions = {}
): T {
  return cleanValue(values, options) as T
}

export function cleanValue<T>(
  values: T,
  options: CleanValuesOptions = {}
): T | null {
  const { toPrimitive, properties } = options
  let cleanedValue: any = values
  if (Array.isArray(cleanedValue)) {
    cleanedValue = cleanedValue.map((item) =>
      cleanValue(item, options)
    )
  } else if (typeof cleanedValue === 'string') {
    cleanedValue = String(cleanedValue).trim()
  } else if (typeof cleanedValue === 'number') {
    return cleanedValue as any
  } else if (typeof cleanedValue === 'boolean') {
    return cleanedValue as any
  } else if (cleanedValue && typeof cleanedValue === 'object') {
    cleanedValue = isFunction(properties)
      ? properties(cleanedValue, options)
      : isArrayWithLength(properties)
      ? pick(properties, cleanedValue)
      : cleanedValue
    cleanedValue = isPlainObject(cleanedValue)
      ? Object.fromEntries(
          Object.entries(cleanedValue).map((entry) => {
            const [key, v] = entry
            if (
              options.shouldCleanProperties &&
              !options.shouldCleanProperties(key, v)
            ) {
              return entry
            }
            return [
              key,
              cleanValue(v, {
                ...options,
                properties: isFunction(properties)
                  ? properties
                  : null,
              }),
            ]
          })
        )
      : cleanedValue
    if (toPrimitive) cleanedValue = toPlainObject(cleanedValue)
  }
  if (isEmpty(cleanedValue) && !Array.isArray(cleanedValue)) {
    return null
  }
  return cleanedValue
}
